import React from 'react';
import { motion } from 'framer-motion';
import { responsiveImg } from '../../lib/imageUrl';
import { useSlotImage } from '../../lib/useSlotImage';
import { useText } from '../../lib/siteContent';
import { UniverseLinks } from './UniverseLinks';

const MAIN_FALLBACK = 'https://images.unsplash.com/photo-1772127822561-35f4e05a004b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const SERVICES = [
  { n: '01', title: 'Henna Time', desc: 'Scénographie, assises, tables basses et textiles.' },
  { n: '02', title: 'Réceptions traditionnelles', desc: 'Célébrations familiales et culturelles.' },
  { n: '03', title: 'Coin d’honneur', desc: 'Un espace central pensé comme une signature.' },
  { n: '04', title: 'Personnalisation', desc: 'Objets, motifs et références familiales.' },
];

const ease = [0.22, 1, 0.36, 1];

const ServiceItem = ({ s }) => (
  <div className="border-t border-[#111111]/15 pt-7" data-testid={`heritage-service-${s.n}`}>
    <span className="block text-[11px] tracking-[0.3em] text-[#6B6B6B]">{s.n}</span>
    <h3 className="mt-4 font-serif font-light text-[#111111] text-2xl md:text-[1.7rem] leading-[1.2]">{s.title}</h3>
    <p className="mt-3 text-sm leading-[1.85] font-light text-[#333333]">{s.desc}</p>
  </div>
);

export const HeritageContent = () => {
  const image = useSlotImage('heritage-main', MAIN_FALLBACK);
  const t = useText();
  return (
    <main data-testid="universe-page-heritage">
      <section className="container-custom pt-16 md:pt-24 lg:pt-32 grid grid-cols-1 lg:grid-cols-[11fr_10fr] gap-x-16 xl:gap-x-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="lg:sticky lg:top-32 self-start"
        >
          <span className="block text-[11px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-8 md:mb-10" data-testid="universe-label">
            {t('heritage.label')}
          </span>
          <h1 className="font-serif font-light text-[#111111] text-5xl sm:text-6xl xl:text-7xl leading-[1.04]" data-testid="universe-title">
            <span className="whitespace-pre-line">{t('heritage.headline')}</span>
          </h1>
          <p className="mt-10 md:mt-12 text-sm md:text-base leading-[1.9] font-light text-[#333333] max-w-lg" data-testid="universe-intro">
            {t('heritage.intro')}
          </p>
          <blockquote className="mt-14 md:mt-20 border-l border-[#111111]/30 pl-6 md:pl-8 max-w-lg" data-testid="heritage-quote">
            <p className="font-serif font-light italic text-[#111111] text-2xl md:text-[1.9rem] leading-[1.35]">
              {t('heritage.quote')}
            </p>
          </blockquote>
        </motion.div>

        <div className="mt-16 lg:mt-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.2 }}
            className="w-full aspect-[4/5] md:aspect-[4/3] lg:aspect-[4/5] overflow-hidden bg-[#F5F5F5]"
          >
            <img {...responsiveImg(image, '(max-width: 768px) 100vw, 50vw', { eager: true })} alt="Détails textiles d'une célébration traditionnelle — image d'illustration" className="w-full h-full object-cover" data-testid="universe-hero-image" />
          </motion.div>

          <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-12" data-testid="heritage-services">
            {SERVICES.map((s, i) => ({ ...s, title: t(`heritage.s${i + 1}.title`), desc: t(`heritage.s${i + 1}.desc`) })).map((s) => <ServiceItem key={s.n} s={s} />)}
          </div>
        </div>
      </section>

      <UniverseLinks />
    </main>
  );
};
